import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import swal from 'sweetalert';

import Client from './Client';
import Config from './ClientConfig';
import active_on from './../images/icons/active_on.png';
import active_off from './../images/icons/active_off.png';

const COUNTY_CACHE = new Map();
const emailValue = (value) => String(value || '').trim().toLowerCase();

export default class Message extends React.Component {
  constructor(props) {
    super(props);
    const today = this.toBusinessDate(new Date().toISOString()).slice(0, 10);
    this.state = {
      client: new Client('none'),
      item: {
        iref: '',
        createdOn: '',            // kept in state, but no longer displayed
        isActive: true,
        dateFrom: props.isNew ? `${today}T00:00:00` : '',
        dateTo: props.isNew ? `${today}T23:59:59` : '',
        sendToHQ: false,
        sendToAllLocationsOwn: false,
        sendToAllLocationsF: false,
        sendToAllClients: false,
        county: null,
        counties: [],
        subject: '',
        message: '',
      },
      // read-only by default; auto-edit only for new items
      edit: props.isNew === true,
      allowEdit: props.allowEdit !== false,
      somethingHasChanged: false,
      isSaving: false,
      justSaved: false,
      counties: [],
      countyOpts: {},
      countyObjs: [],
      targetMode: 'all',
      audienceMode: 'broadcast',
      clientEmail: '',
      clientRecipient: null,
      clientLookupBusy: false,
      clientNotice: null,
      locations: null,
      locationRecipient: null,
      locationLookupBusy: false,
      locationNotice: null,
      durationHours: 24,
    };
    this.originalItem = { ...this.state.item, counties: [] };

    this.handleEditClick = this.handleEditClick.bind(this);
    this.changeValue = this.changeValue.bind(this);
    this.pushData = this.pushData.bind(this);

    this.toPayloadDate = this.toPayloadDate.bind(this);
    this.toYMD = this.toYMD.bind(this);
    this.toDMY = this.toDMY.bind(this);
  }

  componentDidMount() {
    if (this.props.item) {
      const counties = this.countyIds(this.props.item);
      const incoming = {
        ...this.props.item,
        county: counties[0] || null,
        counties,
      };
      this.originalItem = { ...incoming, counties: [...counties] };
      this.setState({
        item: incoming,
        targetMode: counties.length ? 'selected' : 'all',
      }, () => {
        this.updateGeoData('country061', counties);
      });
    } else {
      this.originalItem = { ...this.state.item, counties: [] };
      this.updateGeoData('country061', []);
    }
  }

  componentDidUpdate(prevProps) {
    // Only replace local state when a different item arrives (prevents wiping edits)
    if (
      this.props.item
      && prevProps.item !== this.props.item
      && !this.state.edit
    ) {
      const counties = this.countyIds(this.props.item);
      const incoming = {
        ...this.props.item,
        county: counties[0] || null,
        counties,
      };
      this.originalItem = { ...incoming, counties: [...counties] };
      this.setState({
        item: incoming,
        targetMode: counties.length ? 'selected' : 'all',
        somethingHasChanged: false,
      }, () => {
        this.updateGeoData('country061', counties);
      });
    }
  }

  countyId = (county) => {
    if (county && typeof county === 'object') return county.iref || null;
    return county || null;
  };

  countyIds = (item = {}) => {
    const raw = Array.isArray(item.counties)
      ? item.counties
      : [item.county];
    return raw
      .map((county) => this.countyId(county))
      .filter((county, index, values) => county && values.indexOf(county) === index);
  };

  updateGeoData = (countryId = 'country061', countyIds = []) => {
    let cached = COUNTY_CACHE.get(countryId);
    if (!cached) {
      const data = this.state.client.getData(
        Config.API_URL_COUNTY,
        { country: countryId, orderBy_Name: true }
      );
      const counties = Array.isArray(data?.counties) ? data.counties : [];
      const options = counties.map((county) => ({
        iref: String(county.iref),
        name: String(county.name || ''),
      }));
      const dict = {};
      options.forEach((county) => { dict[county.iref] = county.name; });
      cached = { options, dict };
      COUNTY_CACHE.set(countryId, cached);
    }

    const selectedIds = Array.isArray(countyIds)
      ? countyIds.map(String)
      : (countyIds ? [String(countyIds)] : []);
    const countyObjs = cached.options.filter(
      (county) => selectedIds.includes(county.iref)
    );
    this.setState({
      counties: cached.options,
      countyOpts: cached.dict,
      countyObjs,
    });
  };

  countyNames = (item = {}) => {
    const names = this.countyIds(item)
      .map((countyId) => this.state.countyOpts[countyId])
      .filter(Boolean);
    return names.length ? names.join(', ') : 'All';
  };

  selectedCountyIds = () => (
    this.state.targetMode === 'selected'
      ? this.countyIds(this.state.item)
      : []
  );

  hasBroadcastAudience = (item = {}) => !!(
    item.sendToAllLocationsOwn || item.sendToAllClients
  );

  directRecipient = (item = {}) => {
    const links = Array.isArray(item.messageClient_message) ? item.messageClient_message : [];
    if (
      item.sendToAllClients || item.sendToHQ || item.sendToAllLocationsOwn
      || item.sendToAllLocationsF || links.length !== 1 || !links[0]?.client?.iref
    ) return null;
    const client = links[0].client;
    return {
      iref: client.iref,
      email: client.user?.person?.email || client.user?.email || '',
    };
  };

  directLocationRecipient = (item = {}) => {
    const links = Array.isArray(item.messageLocation_message) ? item.messageLocation_message : [];
    if (
      item.sendToAllClients || item.sendToHQ || item.sendToAllLocationsOwn
      || item.sendToAllLocationsF || links.length !== 1 || !links[0]?.location?.iref
    ) return null;
    const location = links[0].location;
    return {
      iref: location.iref,
      name: location.name || '',
    };
  };

  targetSummary = () => {
    if (!this.state.item.sendToAllClients) {
      return 'County targeting is inactive because Clients is not selected.';
    }
    const count = this.selectedCountyIds().length;
    if (this.state.targetMode === 'all') {
      return 'All counties: one global message will be saved.';
    }
    if (!count) {
      return 'Select at least one county.';
    }
    const prefix = this.state.item.groupId
      ? 'Saving updates'
      : 'Saving creates';
    return `${prefix} ${count} linked message ${count === 1 ? 'record' : 'records'}.`;
  };

  setTargetMode = (_, mode) => {
    if (!mode || !this.state.item.sendToAllClients) return;
    this.setState((state) => ({
      targetMode: mode,
      countyObjs: mode === 'all' ? [] : state.countyObjs,
      item: mode === 'all'
        ? { ...state.item, county: null, counties: [] }
        : state.item,
      somethingHasChanged: true,
    }));
  };

  // ---------- date helpers (robust against T/timezones) ----------
  toBusinessDate(value) {
    const s = String(value || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}[ T].*(?:Z|[+-]\d{2}:?\d{2})$/i.test(s)) return s;
    const instant = new Date(s.replace(' ', 'T'));
    if (Number.isNaN(instant.getTime())) return '';
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Bucharest',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    }).formatToParts(instant).map(({ type, value: part }) => [type, part]));
    return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
  }

  toPayloadDate(raw, endOfDay = false, preserveOffset = false) {
    if (!raw) return null;
    const s = this.toBusinessDate(raw);
    // An untouched API timestamp retains its exact instant, including at DST overlaps.
    const original = String(raw).trim();
    if (preserveOffset && s && /^\d{4}-\d{2}-\d{2}[ T].*(?:Z|[+-]\d{2}:?\d{2})$/i.test(original)) {
      return original.replace(' ', 'T');
    }
    // Date-only To values include the whole day; preserve explicit times.
    const time = (match) => match[4] === undefined
      ? (endOfDay ? '23:59:59' : '00:00:00')
      : `${match[4]}:${match[5]}:${match[6] || '00'}`;

    // YYYY-MM-DD or YYYY-MM-DD[ T]HH:mm[:ss][.sss][Z]
    let m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(s);
    if (m) return `${m[1]}-${m[2]}-${m[3]} ${time(m)}`;

    // DD-MM-YYYY or DD-MM-YYYY[ T]HH:mm[:ss]
    m = /^(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(s);
    if (m) return `${m[3]}-${m[2]}-${m[1]} ${time(m)}`;

    return null;
  }
  toYMD(value) {
    if (!value) return '';
    const s = this.toBusinessDate(value);

    let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s); // handles YYYY-MM-DD, YYYY-MM-DDTHH:mm:ss
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;

    m = /^(\d{2})-(\d{2})-(\d{4})/.exec(s); // handles DD-MM-YYYY
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;

    return '';
  }
  toDMY(value) {
    if (!value) return '';
    const s = this.toBusinessDate(value);

    let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s); // YYYY-MM-DD -> DD-MM-YYYY
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;

    m = /^(\d{2})-(\d{2})-(\d{4})/.exec(s); // already DMY
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;

    return '';
  }

  scheduleTime = (value, endOfDay = false) => (
    this.toPayloadDate(value, endOfDay)?.slice(11, 16)
      || (endOfDay ? '23:59' : '00:00')
  );

  toDateTimeInput = (value, endOfDay = false) => (
    this.toPayloadDate(value, endOfDay)?.slice(0, 16).replace(' ', 'T') || ''
  );

  scheduleLabel = (value, endOfDay = false) => (
    value ? `${this.toDMY(value)} ${this.toPayloadDate(value, endOfDay)?.slice(11, 16) || ''}` : '—'
  );

  changeSchedule = (field, value) => {
    this.setState((state) => {
      const previous = state.item[field];
      let next = value;
      if (next) {
        const oldValue = this.toDateTimeInput(previous, field === 'dateTo');
        const sameTime = oldValue && oldValue.slice(11) === next.slice(11);
        const seconds = sameTime
          ? this.toPayloadDate(previous, field === 'dateTo').slice(17, 19)
          : field === 'dateTo' && next.endsWith('T23:59') ? '59' : '00';
        next = next === oldValue ? previous : `${next}:${seconds}`;
      }
      return {
        item: { ...state.item, [field]: next },
        somethingHasChanged: true,
      };
    });
  };

  hasValidDates = () => {
    const { dateFrom, dateTo } = this.state.item || {};
    if (!dateFrom || !dateTo) return false;
    const d1 = this.toPayloadDate(dateFrom);
    const d2 = this.toPayloadDate(dateTo, true);
    if (!d1 || !d2) return false;
    // Fixed-width business-local values sort chronologically without using the browser timezone.
    return d1 <= d2;
  };

  handleEditClick(){
    if (!this.state.allowEdit) return;
    this.setState({ edit: true });
  }

  handleCancelClick = async () => {
    if (this.props.isNew) {
      if (typeof this.props.onCancel === 'function') this.props.onCancel();
      return;
    }
    if (this.state.somethingHasChanged) {
      const confirmed = await swal({
        title: 'Discard changes?',
        text: 'Your unsaved message changes will be lost.',
        icon: 'warning',
        buttons: ['Keep editing', 'Discard'],
        dangerMode: true,
      });
      if (!confirmed) return;
    }
    const restored = {
      ...this.originalItem,
      counties: [...this.countyIds(this.originalItem)],
    };
    const counties = this.countyIds(restored);
    this.setState({
      item: restored,
      edit: false,
      somethingHasChanged: false,
      targetMode: counties.length ? 'selected' : 'all',
    }, () => this.updateGeoData('country061', counties));
  };

  changeValue(e){
    const id = e.target.id;
    const value = (e.target.type === 'checkbox') ? e.target.checked : e.target.value;
    this.setState(s => ({ item:{...s.item, [id]: value}, somethingHasChanged:true }));
  }

  changeAudienceMode = (_, audienceMode) => {
    if (!audienceMode || this.state.isSaving || this.state.clientLookupBusy || this.state.locationLookupBusy) return;
    this.setState({
      audienceMode,
      clientEmail: '',
      clientRecipient: null,
      clientNotice: null,
      locationRecipient: null,
      locationNotice: null,
    }, () => {
      if (audienceMode === 'location') this.loadLocations();
    });
  };

  loadLocations = async () => {
    if (Array.isArray(this.state.locations) || this.state.locationLookupBusy) return;
    this.setState({ locationLookupBusy: true, locationNotice: null });
    try {
      const data = await this.state.client.getDataAsync(
        Config.API_URL_LOCATIONS_SIMPLE,
        { status: 1, orderBy_name: true },
      );
      const raw = Array.isArray(data?.locations)
        ? data.locations
        : Object.values(data?.locations || {});
      const locations = raw
        .filter((location) => location?.iref && Number(location.status) === 1)
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
      this.setState({
        locations,
        locationNotice: locations.length
          ? null
          : { severity: 'info', text: 'No active locations are available.' },
      });
    } catch (_) {
      this.setState({
        locations: null,
        locationNotice: { severity: 'error', text: 'Active locations could not be loaded. Try again.' },
      });
    } finally {
      this.setState({ locationLookupBusy: false });
    }
  };

  lookupClient = async () => {
    const email = emailValue(this.state.clientEmail);
    this.setState({ clientRecipient: null, clientNotice: null });
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.setState({ clientNotice: { severity: 'error', text: 'Enter a valid email address.' } });
      return;
    }

    this.setState({ clientLookupBusy: true });
    try {
      const data = await this.state.client.getDataAsync(Config.API_URL_CLIENT, { email });
      if (!Array.isArray(data?.clients)) throw new Error('Invalid client lookup response');
      const matches = data.clients.filter((item) => (
        item && !item.erased && emailValue(item.user?.person?.email) === email && item.iref
      ));
      if (matches.length !== 1) {
        this.setState({
          clientNotice: {
            severity: 'error',
            text: matches.length
              ? 'More than one Client matched. The message was not sent.'
              : 'No eligible Client matched that email.',
          },
        });
        return;
      }
      const match = matches[0];
      this.setState({
        clientRecipient: {
          iref: match.iref,
          email,
          name: [match.user?.first_name, match.user?.last_name].filter(Boolean).join(' '),
        },
      });
    } catch (_) {
      this.setState({ clientNotice: { severity: 'error', text: 'Client lookup failed. Try again.' } });
    } finally {
      this.setState({ clientLookupBusy: false });
    }
  };

  sendToClient = async () => {
    const { clientRecipient, durationHours, item, client } = this.state;
    if (!this.state.edit || this.state.isSaving || !clientRecipient || !item.subject.trim() || !item.message.trim()) return;
    this.setState({ isSaving: true, clientNotice: null });
    let result;
    try {
      result = await client.requestJsonAsync(Config.API_URL_MESSAGE_SEND_TO_CLIENT, 'POST', {
        email: clientRecipient.email,
        client_iref: clientRecipient.iref,
        isActive: !!item.isActive,
        subject: item.subject.trim(),
        message: item.message.trim(),
        durationHours,
      });
      if (!result?.iref || String(result.client_iref) !== String(clientRecipient.iref)) {
        throw new Error('Unexpected send response');
      }
    } catch (error) {
      let text = 'The result is uncertain. Check the message list before sending again.';
      if (error?.status === 404 || error?.status === 405) {
        text = 'Client messages are not available on this backend yet.';
      } else if (error?.status === 400) {
        text = 'The Client lookup or message is no longer valid. Look up the Client again.';
        this.setState({ clientRecipient: null });
      } else if (error?.status === 401 || error?.status === 403) {
        text = 'You are not authorized to send Client messages.';
      }
      this.setState({ isSaving: false, clientNotice: { severity: 'error', text } });
      return;
    }
    this.setState({ isSaving: false, clientRecipient: null });
    swal('Saved', `In-app message #${result.iref} was saved for Client #${clientRecipient.iref} with a ${durationHours}-hour period.`, 'success');
    if (typeof this.props.onUpdated === 'function') this.props.onUpdated();
  };

  sendToLocation = async () => {
    const { locationRecipient, durationHours, item, client } = this.state;
    if (!this.state.edit || this.state.isSaving || !locationRecipient || !item.subject.trim() || !item.message.trim()) return;
    this.setState({ isSaving: true, locationNotice: null });
    let result;
    try {
      result = await client.requestJsonAsync(Config.API_URL_MESSAGE_SEND_TO_LOCATION, 'POST', {
        location_iref: locationRecipient.iref,
        isActive: !!item.isActive,
        subject: item.subject.trim(),
        message: item.message.trim(),
        durationHours,
      });
      if (!result?.iref || String(result.location_iref) !== String(locationRecipient.iref)) {
        throw new Error('Unexpected send response');
      }
    } catch (error) {
      let text = 'The result is uncertain. Check the message list before sending again.';
      if (error?.status === 404 || error?.status === 405) {
        text = 'Location messages are not available on this backend yet.';
      } else if (error?.status === 400) {
        text = 'The Location is no longer active or the message is invalid. Select the Location again.';
        this.setState({ locationRecipient: null, locations: null });
      } else if (error?.status === 401 || error?.status === 403) {
        text = 'You are not authorized to send Location messages.';
      }
      this.setState({ isSaving: false, locationNotice: { severity: 'error', text } });
      return;
    }
    this.setState({ isSaving: false, locationRecipient: null });
    swal(
      'Saved',
      `In-app message #${result.iref} was saved for ${locationRecipient.name || 'the selected Location'} with a ${durationHours}-hour period.`,
      'success',
    );
    if (typeof this.props.onUpdated === 'function') this.props.onUpdated();
  };

  // ---------- save ----------
  pushData = async () => {
    if (this.props.isNew && this.state.audienceMode === 'client') return this.sendToClient();
    if (this.props.isNew && this.state.audienceMode === 'location') return this.sendToLocation();
    if (!(this.state.edit && this.state.somethingHasChanged)) { swal('Error', 'Nothing to save', 'error'); return; }
    if (!this.hasValidDates()) { swal('Missing or invalid period', 'Please select both From and To dates (From must be before To).', 'warning'); return; }

    const src = this.state.item;
    if (
      !this.hasBroadcastAudience(src)
      && !this.directRecipient(src)
      && !this.directLocationRecipient(src)
    ) {
      swal('Select an audience', 'Select Locations, Clients, or both before saving.', 'warning');
      return;
    }
    const counties = (
      src.sendToAllClients && this.state.targetMode === 'selected'
        ? this.countyIds(src)
        : []
    );
    if (
      src.sendToAllClients
      && this.state.targetMode === 'selected'
      && counties.length === 0
    ) {
      swal('Select counties', 'Choose at least one county or use All counties.', 'warning');
      return;
    }

    const originalCounties = this.countyIds(this.originalItem);
    const removedCounties = originalCounties.filter(
      (countyId) => !counties.includes(countyId)
    );
    if (src.iref && removedCounties.length) {
      const confirmed = await swal({
        title: 'Remove county copies?',
        text: `${removedCounties.length} linked message ${removedCounties.length === 1 ? 'copy' : 'copies'} will be removed, including their read state.`,
        icon: 'warning',
        buttons: ['Cancel', 'Continue'],
        dangerMode: true,
      });
      if (!confirmed) return;
    }

    this.setState({ isSaving: true });
    try {
      const originalIref = src.iref || null;
      const payload = {
        ...(src.iref ? { iref: src.iref } : {}),
        isActive: !!src.isActive,
        dateFrom: this.toPayloadDate(src.dateFrom, false, true),
        dateTo:   this.toPayloadDate(src.dateTo, true, true),
        sendToHQ: !!src.sendToHQ,
        sendToAllLocationsOwn: !!src.sendToAllLocationsOwn,
        sendToAllLocationsF:   !!src.sendToAllLocationsF,
        sendToAllClients:      !!src.sendToAllClients,
        counties,
        subject: (src.subject || '').trim(),
        message: (src.message || '').trim(),
      };

      const base = Config.API_URL_MESSAGE;
      const r = this.state.client.pushJson(base, payload);

      if (r && (r.iref || r.updateKey)) {
        const next = { ...this.state.item, ...r };
        this.originalItem = {
          ...next,
          counties: [...this.countyIds(next)],
        };
        this.setState({ item: next, somethingHasChanged: false, isSaving: false, edit: false, justSaved: true });
        setTimeout(()=>this.setState({ justSaved: false }), 800);
        swal('Saved', 'Message saved successfully.', 'success', { buttons: false, timer: 1200 });
        if (typeof this.props.onUpdated === 'function') {
          this.props.onUpdated(next, originalIref);
        }
        return;
      }
      const msg = (r && (r.message || (r.errors && JSON.stringify(r.errors)))) || 'Unknown error';
      swal('Save failed', msg, 'error'); this.setState({ isSaving: false });
    } catch (e) {
      const msg = (e && e.message) ? e.message : String(e);
      swal('Save failed', msg, 'error'); this.setState({ isSaving: false });
    }
  };

  // ---------- render ----------
  render() {
    const p = this.state.item || {};
    const direct = this.directRecipient(p);
    const directLocation = this.directLocationRecipient(p);
    const directCreate = !!this.props.isNew && this.state.audienceMode !== 'broadcast';
    const clientDirectCreate = directCreate && this.state.audienceMode === 'client';
    const locationDirectCreate = directCreate && this.state.audienceMode === 'location';
    const broadcastAudienceMissing = !directCreate && !direct && !directLocation
      && !this.hasBroadcastAudience(p);
    const activeAvatar = p.isActive ? active_on : active_off; 
    const activeAlt = p.isActive ? 'Active' : 'Inactive';
    const countyTargetEnabled = !!p.sendToAllClients;
    const invalidPeriod = !!p.dateFrom && !!p.dateTo && !this.hasValidDates();
    const selectedCountyCount = this.selectedCountyIds().length;
    const countyTargetValid = (
      !countyTargetEnabled
      || this.state.targetMode === 'all'
      || selectedCountyCount > 0
    );

    // Inline header: Subject + isActive in one row, smaller controls
    const headerTitle = this.state.edit ? (
      <Box sx={{ display:'grid', gridTemplateColumns:'1fr auto', alignItems:'center', gap: 1 }}>
        <TextField
          id="subject"
          placeholder="Subject"
          value={p.subject || ''}
          onChange={this.changeValue}
          variant="outlined"
          size="small"
          inputProps={{ maxLength: 128 }}
          fullWidth
        />
        <FormControlLabel
          sx={{ m: 0 }}
          control={
            <Switch
              size="small"
              id="isActive"
              checked={!!p.isActive}
              onChange={(e)=>this.changeValue({target:{id:'isActive', type:'checkbox', checked:e.target.checked}})}
            />
          }
          label="Active"
        />
      </Box>
    ) : (
      <Box sx={{ display:'flex', alignItems:'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
          {p.subject || 'New Message'}
        </Typography>
        <Chip
          size="small"
          label={p.isActive ? 'Active' : 'Inactive'}
          color={p.isActive ? 'success' : 'default'}
          variant="outlined"
        />
        <Chip
          size="small"
          label={
            direct ? 'One Client' : directLocation ? 'One Location' : this.countyIds(p).length
              ? `${this.countyIds(p).length} ${this.countyIds(p).length === 1 ? 'county' : 'counties'}`
              : 'All counties'
          }
          color="primary"
          variant="outlined"
        />
      </Box>
    );

    const audienceControls = (
      <FormGroup row sx={{ gap: 1 }}>
        {[
          // ['sendToHQ', 'HQ'],
          ['sendToAllLocationsOwn', 'Locations'],
          // ['sendToAllLocationsF', 'Franchise'],
          ['sendToAllClients', 'Clients'],
        ].map(([id, label]) => (
          <FormControlLabel
            key={id}
            sx={{ m: 0 }}
            control={
              <Checkbox
                size="small"
                sx={{ p: 0.5 }}
                id={id}
                checked={!!p[id]}
                onChange={(e)=>this.changeValue({target:{id, type:'checkbox', checked:e.target.checked}})}
              />
            }
            label={label}
          />
        ))}
      </FormGroup>
    );

    // Edit view (compact)
    const detailsEdit = (
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(280px, 340px)', lg: 'minmax(0, 1fr) minmax(240px, 280px) minmax(260px, 320px)' },
        gap: 1,
        alignItems: 'start',
      }}>
        {/* Fill the adjacent controls' height when displayed side by side. */}
        <TextField
          id="message"
          label="Message"
          value={p.message || ''}
          onChange={this.changeValue}
          variant="outlined"
          size="small"
          multiline
          minRows={4}
          maxRows={10}
          inputProps={{ maxLength: 1024 }}
          sx={{
            alignSelf: { xs: 'start', md: 'stretch' },
            '& .MuiInputBase-root': {
              flex: { md: 1 },
              alignItems: 'flex-start',
            },
            '& textarea:not([aria-hidden="true"])': {
              height: { md: '100% !important' },
              overflowY: { md: 'auto !important' },
              boxSizing: 'border-box',
            },
          }}
        />

        {/* Right: Period + Audience */}
        <Box sx={{ display: { xs: 'grid', lg: 'contents' }, gap: 1 }}>
          <Box sx={{ p: 0.5, minWidth: 0 }}>
            {directCreate ? (
              <>
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Availability</Typography>
                <TextField
                  select
                  label="Available for"
                  size="small"
                  fullWidth
                  value={this.state.durationHours}
                  onChange={(event) => this.setState({ durationHours: Number(event.target.value) })}
                >
                  {[24, 48, 72].map((hours) => (
                    <MenuItem key={hours} value={hours}>{hours} hours</MenuItem>
                  ))}
                </TextField>
                <Typography variant="caption" color="text.secondary">
                  {clientDirectCreate
                    ? 'The message appears in the app when the Client next loads messages.'
                    : 'Visible in-app to Location and POS users assigned to the selected Location.'}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Schedule</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 1 }}>
                  {[
                    ['dateFrom', 'From', false],
                    ['dateTo', 'To', true],
                  ].map(([field, label, endOfDay]) => (
                    <TextField
                      key={field}
                      id={`${field}_ui`}
                      label={label}
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={this.toDateTimeInput(p[field], endOfDay)}
                      onChange={(e) => this.changeSchedule(field, e.target.value)}
                      type="datetime-local"
                      inputProps={{ step: 60 }}
                      InputLabelProps={{ shrink: true }}
                      error={!p[field] || (endOfDay && invalidPeriod)}
                      helperText={!p[field] ? 'Required' : ''}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color={invalidPeriod ? 'error.main' : 'text.secondary'}>
                  {invalidPeriod ? 'To must be on or after From. ' : ''}Times: Europe/Bucharest
                </Typography>
              </>
            )}
          </Box>

          <Box sx={{ p: 0.5, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="subtitle2">Audience &amp; targeting</Typography>
              {broadcastAudienceMissing ? (
                <Tooltip title="Select Locations, Clients, or both before saving." arrow enterTouchDelay={0}>
                  <IconButton
                    aria-label="Select Locations, Clients, or both before saving."
                    color="warning"
                    size="small"
                    sx={{ p: 0 }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
            {this.props.isNew ? (
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={this.state.audienceMode}
                onChange={this.changeAudienceMode}
                disabled={this.state.clientLookupBusy || this.state.locationLookupBusy || this.state.isSaving}
                aria-label="Message audience"
                sx={{
                  my: 0.5,
                  '& .MuiToggleButton-root': {
                    minHeight: 27,
                    py: 0.125,
                    px: 0.5,
                    fontSize: '0.72rem',
                    lineHeight: 1.25,
                    textTransform: 'none',
                  },
                }}
              >
                <ToggleButton value="broadcast" aria-label="Broadcast">Broadcast</ToggleButton>
                <ToggleButton value="client" aria-label="One Client">1 Client</ToggleButton>
                <ToggleButton value="location" aria-label="One Location">1 Location</ToggleButton>
              </ToggleButtonGroup>
            ) : null}
            <Box sx={{ minHeight: this.props.isNew ? 150 : 0 }}>
              {clientDirectCreate ? (
                <Box sx={{ display: 'grid', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    type="email"
                    label="Find Client by email"
                    size="small"
                    value={this.state.clientEmail}
                    disabled={this.state.clientLookupBusy || this.state.isSaving}
                    onChange={(event) => this.setState({
                      clientEmail: event.target.value,
                      clientRecipient: null,
                      clientNotice: null,
                    })}
                    sx={{ flex: '1 1 200px' }}
                  />
                  <Button
                    variant="outlined"
                    disabled={this.state.clientLookupBusy || this.state.isSaving || !this.state.clientEmail.trim()}
                    onClick={this.lookupClient}
                  >
                    {this.state.clientLookupBusy ? 'Looking up…' : 'Find Client'}
                  </Button>
                </Box>
                {this.state.clientRecipient ? (
                  <Typography variant="body2" color="success.main" role="status">
                    Recipient confirmed: {this.state.clientRecipient.name ? `${this.state.clientRecipient.name} · ` : ''}
                    Client #{this.state.clientRecipient.iref}
                  </Typography>
                ) : null}
                {this.state.clientNotice ? (
                  <Alert severity={this.state.clientNotice.severity} role="alert">
                    {this.state.clientNotice.text}
                  </Alert>
                ) : null}
                </Box>
              ) : locationDirectCreate ? (
                <Box sx={{ display: 'grid', gap: 0.75 }}>
                <Autocomplete
                  size="small"
                  options={this.state.locations || []}
                  loading={this.state.locationLookupBusy}
                  onOpen={this.loadLocations}
                  value={this.state.locationRecipient}
                  onChange={(_, value) => this.setState({
                    locationRecipient: value,
                    locationNotice: null,
                  })}
                  getOptionLabel={(option) => option?.iref
                    ? (option.name || 'Location')
                    : ''}
                  isOptionEqualToValue={(option, value) => String(option?.iref) === String(value?.iref)}
                  disabled={this.state.locationLookupBusy || this.state.isSaving}
                  noOptionsText="No active locations"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Active Location"
                      placeholder="Select a Location"
                    />
                  )}
                />
                <Typography variant="caption" color="text.secondary">
                  All Location and POS users assigned to this Location will see the message in-app.
                </Typography>
                {this.state.locationRecipient ? (
                  <Typography variant="body2" color="success.main" role="status">
                    Recipient confirmed: {this.state.locationRecipient.name || 'Selected Location'}
                  </Typography>
                ) : null}
                {this.state.locationNotice ? (
                  <Alert severity={this.state.locationNotice.severity} role="alert">
                    {this.state.locationNotice.text}
                  </Alert>
                ) : null}
                </Box>
              ) : direct ? (
                <Typography variant="body2">One Client: {direct.email || `#${direct.iref}`}</Typography>
              ) : directLocation ? (
                <Typography variant="body2">
                  One Location: {directLocation.name || 'Selected Location'}
                </Typography>
              ) : audienceControls}
              {!directCreate && !direct && !directLocation ? (
                <>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={this.state.targetMode}
                  onChange={this.setTargetMode}
                  disabled={!countyTargetEnabled}
                  sx={{
                    my: 0.5,
                    '& .MuiToggleButton-root': {
                      minHeight: 30,
                      py: 0.25,
                      px: 1,
                      fontSize: '0.75rem',
                      lineHeight: 1.5,
                    },
                  }}
                >
                  <ToggleButton value="all">All counties</ToggleButton>
                  <ToggleButton value="selected">Selected counties</ToggleButton>
                </ToggleButtonGroup>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  disabled={!countyTargetEnabled || this.state.targetMode === 'all'}
                  options={this.state.counties}
                  getOptionLabel={(option) => option?.name || ''}
                  isOptionEqualToValue={(option, value) => option?.iref === value?.iref}
                  value={this.state.countyObjs}
                  limitTags={2}
                  ListboxProps={{
                    sx: {
                      maxHeight: 112,
                      py: 0.25,
                      '& .MuiAutocomplete-option': {
                        minHeight: 32,
                        py: 0.25,
                        fontSize: '0.8125rem',
                      },
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                      minHeight: 34,
                      maxHeight: 72,
                      py: 0.25,
                      overflowY: 'auto',
                      alignItems: 'center',
                    },
                    '& .MuiAutocomplete-tag': { height: 22, my: 0.25 },
                  }}
                  size="small"
                  onChange={(_, value) => this.setState((state) => ({
                    countyObjs: value || [],
                    item: {
                      ...state.item,
                      county: value?.[0]?.iref || null,
                      counties: (value || []).map((county) => county.iref),
                    },
                    somethingHasChanged: true,
                  }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Client counties"
                      error={!countyTargetValid}
                      fullWidth
                    />
                  )}
                />
                <Box sx={{ mt: 0.5 }}>
                  <Typography
                    variant="caption"
                    color={countyTargetValid ? 'text.secondary' : 'error.main'}
                  >
                    {this.targetSummary()}
                  </Typography>
                </Box>
                </>
              ) : null}
            </Box>
          </Box>
        </Box>
      </Box>
    );

    // Read-only view (compact)
    const detailsReadOnly = (
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(300px, 360px)' },
        gap: 1,
        alignItems: 'start',
      }}>
        {/* Left: Message */}
        <Box sx={{ p: 0.75 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Message</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace:'pre-wrap' }}>
            {p.message || '—'}
          </Typography>
        </Box>

        {/* Right: Period + Audience */}
        <Box sx={{ display:'grid', gap: 1 }}>
          <Box sx={{ px: 0.75 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Schedule</Typography>
            <Typography variant="body2" color="text.secondary">
              From: {p.dateFrom ? `${this.toDMY(p.dateFrom)} ${this.scheduleTime(p.dateFrom)}` : '—'}
              <br />
              To: {p.dateTo ? `${this.toDMY(p.dateTo)} ${this.scheduleTime(p.dateTo, true)}` : '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">Times: Europe/Bucharest</Typography>
          </Box>

          <Box sx={{ px: 0.75 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Audience &amp; targeting</Typography>
            {direct ? (
              <Typography variant="body2" color="text.secondary">
                One Client: {direct.email || `#${direct.iref}`}
              </Typography>
            ) : directLocation ? (
              <Typography variant="body2" color="text.secondary">
                One Location: {directLocation.name || 'Selected Location'}
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  Locations: {p.sendToAllLocationsOwn ? 'Yes' : 'No'} • Clients: {p.sendToAllClients ? 'Yes' : 'No'}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ overflowWrap: 'anywhere' }}
                >
                  Client counties: {this.countyNames(p)}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
    );

    const saveEnabled = (
      this.state.edit
      && !this.state.isSaving
      && (directCreate
        ? !!(clientDirectCreate ? this.state.clientRecipient : this.state.locationRecipient)
          && !!p.subject.trim()
          && !!p.message.trim()
          && !this.state.clientLookupBusy
          && !this.state.locationLookupBusy
        : this.state.somethingHasChanged && this.hasValidDates() && countyTargetValid
          && !broadcastAudienceMissing)
    );

    return (
      <Card sx={{ borderRadius: '14px', m: 1, border: this.state.justSaved ? '2px solid #4caf50' : '1px solid #e0e0e0', transition: 'border 0.3s ease' }}>
        <CardHeader
          avatar={<Avatar alt={activeAlt} src={activeAvatar} title={activeAlt} sx={{ width: 28, height: 28 }} />}
          title={headerTitle}
          subheader={p.iref
            ? `Message #${p.iref}`
            : clientDirectCreate
              ? 'New message for one Client'
              : locationDirectCreate
                ? 'New message for one Location'
                : 'New message'}
          disableTypography
          sx={{
            p: 1,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: 0.75,
            '& .MuiCardHeader-avatar': { mr: 0 },
            '& .MuiCardHeader-content': { minWidth: 0 },
            '& .MuiCardHeader-action': { alignSelf: 'center', m: 0, ml: 'auto' },
          }}
          action={
            <Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>
              {this.state.edit ? (
                <>
                  <Button
                    variant="text"
                    size="small"
                    disabled={this.state.isSaving}
                    onClick={this.handleCancelClick}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={(
                      <Badge
                        color="warning"
                        variant="dot"
                        overlap="circular"
                        invisible={!this.state.somethingHasChanged || this.state.isSaving}
                      >
                        <SaveIcon fontSize="small" />
                      </Badge>
                    )}
                    disabled={!saveEnabled}
                    onClick={this.pushData}
                    title={this.state.somethingHasChanged && !this.state.isSaving ? 'Unsaved changes' : undefined}
                    sx={{ minWidth: 84 }}
                  >
                    {this.state.isSaving ? 'Saving…' : 'Save'}
                  </Button>
                </>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={this.handleEditClick}
                  disabled={!this.state.allowEdit}
                >
                  Edit
                </Button>
              )}
            </Box>
          }
        />

        <Divider />

        {/* Always visible content (no collapsed layout) */}
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          {this.state.edit ? detailsEdit : detailsReadOnly}
        </CardContent>
      </Card>
    );
  }
}
